import React from 'react';
import { Link } from 'react-router-dom';
import { CheckBadgeIcon } from '@heroicons/react/20/solid';
import { BuildingStorefrontIcon, UserIcon } from '@heroicons/react/24/outline';
import type { Contributor } from '../../types/buildExpansion';
import { resolvePublicFileUrl } from '../../utils/imageUrls';

/**
 * Who worked on this build. Shop contributors link to the shop page, user
 * contributors link to their profile, credit-only entries render as plain
 * text. Roles are always explicit; pending entries carry a quiet tag.
 */

const formatDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
};

const formatDateRange = (from: string | null, to: string | null): string | null => {
  const fromLabel = formatDate(from);
  const toLabel = formatDate(to);
  if (fromLabel && toLabel) return `${fromLabel} to ${toLabel}`;
  if (fromLabel) return fromLabel;
  return null;
};

const CARD_CLASS =
  'flex items-start gap-3 rounded-lg border border-white/[0.06] bg-[#0c0c0c]/50 px-3.5 py-3 min-h-[44px]';
const LINK_CARD_CLASS = `${CARD_CLASS} cursor-pointer transition-colors duration-200 hover:border-white/[0.14] focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-400`;

const ContributorBody: React.FC<{ contributor: Contributor; nameNode: React.ReactNode }> = ({
  contributor,
  nameNode,
}) => {
  const dateRange = formatDateRange(contributor.date_from, contributor.date_to);
  const isPending = contributor.status.startsWith('pending');

  return (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
        {nameNode}
        {contributor.contributor_type === 'shop' && contributor.shop_verified && (
          <span className="inline-flex" title="Verified shop">
            <CheckBadgeIcon className="w-4 h-4 text-emerald-400 shrink-0" aria-hidden />
            <span className="sr-only">Verified shop</span>
          </span>
        )}
        {isPending && (
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 border border-white/[0.08] rounded px-1.5 py-0.5">
            Pending
          </span>
        )}
      </div>
      <p className="text-xs font-medium text-orange-300/90 mt-0.5">{contributor.role}</p>
      {contributor.work_description && (
        <p className="text-xs text-gray-400 mt-1 leading-relaxed">{contributor.work_description}</p>
      )}
      {dateRange && <p className="text-[11px] text-gray-500 mt-1">{dateRange}</p>}
    </div>
  );
};

const ShopAvatar: React.FC<{ contributor: Contributor }> = ({ contributor }) => {
  const logoUrl = resolvePublicFileUrl(contributor.shop_logo);
  return logoUrl ? (
    <img
      src={logoUrl}
      alt=""
      className="w-10 h-10 rounded-lg object-cover ring-1 ring-white/[0.08] shrink-0"
    />
  ) : (
    <span className="w-10 h-10 rounded-lg bg-[#111] flex items-center justify-center ring-1 ring-white/[0.08] shrink-0">
      <BuildingStorefrontIcon className="w-5 h-5 text-gray-400" aria-hidden />
    </span>
  );
};

const UserAvatar: React.FC<{ name: string }> = ({ name }) => (
  <span className="w-10 h-10 rounded-full bg-[#111] flex items-center justify-center ring-1 ring-white/[0.08] shrink-0 text-sm font-semibold text-gray-300">
    {name.trim() ? name.trim().charAt(0).toUpperCase() : <UserIcon className="w-5 h-5 text-gray-400" aria-hidden />}
  </span>
);

export interface ContributorCardsProps {
  contributors: Contributor[];
  className?: string;
}

export const ContributorCards: React.FC<ContributorCardsProps> = ({
  contributors,
  className = '',
}) => {
  const shown = contributors.filter((c) => c.status !== 'declined' && c.status !== 'revoked');
  if (shown.length === 0) return null;

  return (
    <section className={`card p-5 sm:p-6 ${className}`} aria-label="Contributors">
      <h3 className="text-sm font-semibold text-white">Contributors</h3>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-3 list-none">
        {shown.map((contributor) => {
          const displayName = contributor.name || contributor.username || 'Contributor';

          if (contributor.contributor_type === 'shop' && contributor.shop_slug) {
            return (
              <li key={contributor.id}>
                <Link to={`/shops/${contributor.shop_slug}`} className={LINK_CARD_CLASS}>
                  <ShopAvatar contributor={contributor} />
                  <ContributorBody
                    contributor={contributor}
                    nameNode={
                      <span className="text-sm font-medium text-white truncate">{displayName}</span>
                    }
                  />
                </Link>
              </li>
            );
          }

          if (contributor.contributor_type === 'user' && contributor.username) {
            return (
              <li key={contributor.id}>
                <Link to={`/${contributor.username}`} className={LINK_CARD_CLASS}>
                  <UserAvatar name={displayName} />
                  <ContributorBody
                    contributor={contributor}
                    nameNode={
                      <span className="text-sm font-medium text-white truncate">
                        {contributor.name || `@${contributor.username}`}
                      </span>
                    }
                  />
                </Link>
              </li>
            );
          }

          // Credit-only entry, or a shop/user entry missing its link target.
          return (
            <li key={contributor.id}>
              <div className={CARD_CLASS}>
                <UserAvatar name={displayName} />
                <ContributorBody
                  contributor={contributor}
                  nameNode={
                    <span className="text-sm font-medium text-gray-200 truncate">{displayName}</span>
                  }
                />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default ContributorCards;
